import React, { useState, useEffect, useRef } from 'react';
import { PhoneCall, Mail, Send, Building2 } from 'lucide-react';
import { useScrollAnimation } from '../utils/useScrollAnimations';
import GDPRModal from './GDPRModal';
import TermsModal from './TermsModal';

const ContactForm = ({ isDarkMode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { scrollY } = useScrollAnimation();
  const [formData, setFormData] = useState({
    ico: '',
    phone: '',
    email: '',
    company: '',
    address: '',
    message: '',
    gdprAccepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const [showGDPR, setShowGDPR] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  // Funkce pro získání dat společnosti podle IČO z ARES
  const fetchCompanyData = async (ico) => {
    try {
      setIsLoadingCompany(true);
      
      // Reálné API volání k ARES
      const response = await fetch(`https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/${ico}`);
      
      if (response.ok) {
        const data = await response.json();
        const companyName = data.obchodniJmeno || data.nazev;
        const address = data.sidlo ? 
          `${data.sidlo.nazevUlice || ''} ${data.sidlo.cisloDomovni || ''}, ${data.sidlo.psc || ''} ${data.sidlo.nazevObce || ''}`.trim() :
          '';
        
        if (companyName) {
          setFormData(prev => ({
            ...prev,
            company: companyName,
            address: address
          }));
        }
      } else {
        // Fallback mock data pokud ARES API nefunguje
        const mockCompanies = {
          '12345678': {
            name: 'ACME s.r.o.',
            address: 'Wenceslas Square 1, 110 00 Prague 1'
          },
          '87654321': {
            name: 'Technologie Plus a.s.',
            address: 'Náměstí Míru 15, 120 00 Prague 2'
          },
          '25596641': {
            name: 'O2 Czech Republic a.s.',
            address: 'Za Brumlovkou 266/2, 140 22 Praha 4'
          },
          '00025593': {
            name: 'Vodafone Czech Republic a.s.',
            address: 'náměstí Junkových 2808/2, 155 00 Praha 5'
          }
        };
        
        const companyData = mockCompanies[ico];
        if (companyData) {
          setFormData(prev => ({
            ...prev,
            company: companyData.name,
            address: companyData.address
          }));
        }
      }
    } catch (error) {
      console.log('Chyba při načítání dat společnosti:', error);
    } finally {
      setIsLoadingCompany(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    
    // Automatické doplnění předvolby +420 pro telefon
    if (name === 'phone') {
      // Pokud začíná uživatel psát číslo a nezačíná +420, přidej to
      if (value.length > 0 && !value.startsWith('+420') && /^\d/.test(value)) {
        processedValue = '+420 ' + value;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));

    // Automatické doplnění společnosti při zadání IČO
    if (name === 'ico' && value.length === 8 && /^\d{8}$/.test(value)) {
      fetchCompanyData(value);
    }
    
    // Vymaž název společnosti a adresu pokud se IČO změní
    if (name === 'ico' && value.length !== 8) {
      setFormData(prev => ({
        ...prev,
        company: '',
        address: ''
      }));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const cleanPhone = phone.replace(/[\s\-+()]/g, '');
    return /^\d{9,}$/.test(cleanPhone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.gdprAccepted) {
      setSubmitStatus({ type: 'error', message: 'Musíte souhlasit se zpracováním osobních údajů a obchodními podmínkami' });
      return;
    }

    if (!validateEmail(formData.email)) {
      setSubmitStatus({ type: 'error', message: 'Prosím zadejte platný e-mail' });
      return;
    }
    
    if (formData.phone && !validatePhone(formData.phone)) {
      setSubmitStatus({ type: 'error', message: 'Prosím zadejte platné telefonní číslo' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const leadData = {
        ico: formData.ico,
        phone: formData.phone,
        email: formData.email,
        company: formData.company,
        address: formData.address,
        message: formData.message,
        timestamp: new Date().toISOString(),
        source: 'vyjedname-web'
      };

      console.log('Odesílám lead data do CRM:', leadData);
      
      // Seznam možných CRM backend URL (primární a záložní)
      const crmUrls = [
        'https://crm-backend-6jqe.onrender.com/api/web-lead',
        'http://localhost:5000/api/web-lead'
      ];
      
      let crmResponse = null;
      let lastError = null;
      
      // Zkusíme jednotlivé URL dokud nějaké nefunguje
      for (const url of crmUrls) {
        try {
          console.log(`Zkouším odeslat na: ${url}`);
          
          crmResponse = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(leadData),
            timeout: 15000
          });
          
          if (crmResponse.ok) {
            console.log(`✅ Úspěšně odesláno na: ${url}`);
            break; // Úspěch, ukončíme smyčku
          } else {
            throw new Error(`HTTP ${crmResponse.status}: ${crmResponse.statusText}`);
          }
          
        } catch (error) {
          console.warn(`❌ Chyba při odesílání na ${url}:`, error.message);
          lastError = error;
          crmResponse = null;
        }
      }

      if (!crmResponse || !crmResponse.ok) {
        console.error('Nepodařilo se spojit s CRM API, poslední chyba:', lastError?.message);
        
        // Pokusíme se poslat alespoň email notifikaci
        try {
          const fallbackResponse = await fetch('https://formspree.io/f/xpwzdlal', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: leadData.email,
              subject: `Nový web lead: ${leadData.company || leadData.ico}`,
              message: `
CRM API bylo nedostupné, prosím zpracujte ručně:

IČO: ${leadData.ico}
Společnost: ${leadData.company || 'Není uvedeno'}
Email: ${leadData.email}
Telefon: ${leadData.phone || 'Není uvedeno'}
Adresa: ${leadData.address || 'Není uvedeno'}
Zpráva: ${leadData.message || 'Žádná'}
Čas: ${leadData.timestamp}
Zdroj: ${leadData.source}
              `
            })
          });
          
          if (fallbackResponse.ok) {
            setSubmitStatus({ 
              type: 'success', 
              message: 'Vaše poptávka byla odeslána. CRM systém je dočasně nedostupný, ale vaše zpráva byla doručena emailem. Ozveme se vám do 24 hodin.' 
            });
          } else {
            throw new Error('Také fallback selhalo');
          }
          
        } catch (fallbackError) {
          console.error('Také fallback email selhal:', fallbackError);
          throw new Error(`CRM API nedostupné: ${lastError?.message || 'Neznámá chyba'}`);
        }
      } else {
        // CRM API odpovědělo úspěšně
        const crmResult = await crmResponse.json();
        console.log('CRM response:', crmResult);
        
        setSubmitStatus({ 
          type: 'success', 
          message: 'Děkujeme! Vaše poptávka byla úspěšně odeslána. Ozveme se vám do 24 hodin.' 
        });
      }
      
      setFormData({
        ico: '',
        phone: '',
        email: '',
        company: '',
        address: '',
        message: '',
        gdprAccepted: false
      });

    } catch (error) {
      console.error('Chyba při odesílání do CRM:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'Došlo k chybě při odesílání. Zkuste to prosím znovu nebo nás kontaktujte přímo.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      ref={sectionRef}
      id="contact" 
      className="py-12 relative scroll-optimized bg-gradient-to-b from-white via-red-50/30 to-gray-50"
    >
      {/* Simplified background elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 right-20 w-24 h-24 bg-red-500 rounded-full filter blur-xl parallax-light"></div>
        <div className="absolute bottom-20 left-20 w-32 h-32 bg-red-600 rounded-full filter blur-xl parallax-light"></div>
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className={`text-center mb-12 section-transition ${isVisible ? 'visible' : ''}`}>
          <h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{
              transform: `translateY(${Math.sin(scrollY * 0.01) * 2}px)`
            }}
          >
            <span className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
              Získejte nabídku na míru
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-red-500 to-red-600 mx-auto mb-6"></div>
          <p className="text-lg max-w-3xl mx-auto text-gray-700">
            Necháte-li nám kontakt, připravíme vám personalizovanou nabídku během 24 hodin
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            {/* Contact Information */}
            <div 
              className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
            >
              <div 
                className="rounded-2xl p-8 bg-white border border-gray-200 shadow-sm relative overflow-hidden h-full flex flex-col"
              >
                
                <div className="relative z-10">
                  <div className="mb-6">
                    <h3 
                      className="text-2xl font-bold text-gray-900 transition-all duration-300"
                    >
                      Kontaktujte nás
                    </h3>
                  </div>
                
                {/* Kontaktní údaje */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-white border-2 border-red-500 rounded-lg flex items-center justify-center mr-4">
                      <PhoneCall size={20} className="text-red-500" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">David Formánek</h4>
                      <a href="tel:+420775796960" className="text-gray-600 hover:text-red-600 underline decoration-dotted cursor-pointer transition-colors">+420 775 796 960</a> / <a href="tel:+420776457223" className="text-gray-600 hover:text-red-600 underline decoration-dotted cursor-pointer transition-colors">+420 776 457 223</a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-white border-2 border-red-500 rounded-lg flex items-center justify-center mr-4">
                      <PhoneCall size={20} className="text-red-500" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Jakub Dufek</h4>
                      <a href="tel:+420604900400" className="text-gray-600 hover:text-red-600 underline decoration-dotted cursor-pointer transition-colors">+420 604 900 400</a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-white border-2 border-red-500 rounded-lg flex items-center justify-center mr-4">
                      <Mail size={20} className="text-red-500" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Email</h4>
                      <p className="text-gray-600">info@vyjedname.cz</p>
                    </div>
                  </div>
                </div>

                {/* Vodafone Partnership Logo */}
                <div className="mb-8 flex justify-start">
                  <img 
                    src="/vodafone-partner-business.png" 
                    alt="Vodafone Partner Business" 
                    className="h-24 w-auto"
                  />
                </div>

                {/* Firemní údaje */}
                <div className="mt-auto p-4 rounded-lg bg-gray-50 border border-gray-200">
                  <h4 className="font-semibold mb-3 text-gray-900">
                    Firemní údaje
                  </h4>
                  <div className="text-sm space-y-1 text-gray-600">
                    <p><strong>Vyjednáme, s.r.o.</strong></p>
                    <p>IČO: 21377596</p>
                    <p>DIČ: CZ21377596</p>
                    <p>Sídlo: Příčná 1892/4, Nové Město (Praha 1), 110 00 Praha</p>
                    <p>Bankovní spojení: 6706235319/0800</p>
                  </div>
                </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="rounded-2xl p-8 bg-white border border-gray-200 shadow-sm h-full flex flex-col">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* IČO a automatické doplnění společnosti */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      IČO *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="ico"
                        value={formData.ico}
                        onChange={handleInputChange}
                        maxLength="8"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 transition-colors focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="12345678"
                      />
                      {isLoadingCompany && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500"></div>
                        </div>
                      )}
                    </div>
                    <p className="text-xs mt-1 text-gray-500">
                      Název společnosti s.r.o.
                    </p>
                  </div>

                  {/* Název společnosti - automaticky doplněný */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      Společnost s.r.o.
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border transition-colors focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400 ${
                        formData.company && formData.ico.length === 8 ? 'border-green-400 bg-green-50' : 'border-gray-300'
                      }`}
                      placeholder="Název se doplní automaticky dle IČO"
                      readOnly={formData.ico.length === 8 && formData.company}
                    />
                  </div>

                  {/* Email & Phone */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        E-mail *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 transition-colors focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="vas@email.cz"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        Telefon
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 transition-colors focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="+420 xxx xxx xxx"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      Zpráva (volitelné)
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 transition-colors focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                      placeholder="Popište vaše požadavky nebo nám dejte vědět, s čím vám můžeme pomoci..."
                    />
                  </div>

                  {/* Submit Status */}
                  {submitStatus && (
                    <div className={`p-4 rounded-lg ${
                      submitStatus.type === 'success' 
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : 'bg-red-100 text-red-700 border border-red-200'
                    }`}>
                      {submitStatus.message}
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="gdprAccepted"
              name="gdprAccepted"
              checked={formData.gdprAccepted}
              onChange={handleInputChange}
              className="mt-1 h-5 w-5 text-purple-600 bg-gray-800 border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
              required
            />
            <label htmlFor="gdprAccepted" className="text-sm text-gray-600 leading-relaxed">
              Souhlasím s <button type="button" onClick={() => setShowGDPR(true)} className="text-red-600 hover:text-red-700 underline">GDPR</button> a <button type="button" onClick={() => setShowTerms(true)} className="text-red-600 hover:text-red-700 underline">obchodními podmínkami</button>.<br />Bez tohoto souhlasu není možné odeslat nabídku.
            </label>
          </div>

          <button
                    type="submit"
                    disabled={isSubmitting || !formData.email || !formData.ico}
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-gray-500 disabled:to-gray-600 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                        Odesíláme...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <Send size={18} />
                        Získat nabídku zdarma
                      </div>
                    )}
                  </button>

                  <p className="text-sm text-center text-gray-500">
                    * Povinné pole.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <GDPRModal isOpen={showGDPR} onClose={() => setShowGDPR(false)} />
      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
    </section>
  );
};

export default ContactForm;
